import React, { forwardRef, useState, useRef, useImperativeHandle } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Row, Col, Button, Input, Form } from 'antd';

import Modal from 'common/components/modal';
import Articles from './articles';
import ScheduleModal from './schedule';

import { MUTATION_CREATE_RELEASE_NOTE } from '../queries';

const useStyles = makeStyles(theme => ({
  wrapper: {
    '& .ant-modal-header': {
      paddingLeft: 60,
      paddingRight: 60,
    },

    '& .ant-modal-body': {
      padding: '0 60px',
    },

    '& .ant-form-item-label': {
      lineHeight: '22px !important',
      color: theme.colors.titleColor,
      marginBottom: 8,
    },

    '& .optional-label': {
      color: theme.colors.captionColor,
    },
  },
  defaultBtn: {
    borderColor: '#d9d9d9',
  },
  primaryBtn: {
    '& > span': {
      fontWeight: '600 !important',
    },
  },
}));

export const DRAFT = 'DRAFT';
export const PUBLISHED = 'PUBLISHED';

const CreateReleaseModal = forwardRef(({ form, refetchData }, ref) => {
  const { getFieldDecorator, validateFields, getFieldsValue } = form;
  const [visible, setVisible] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();

  const scheduleModalRef = useRef(null);

  const handleCancelModal = () => {
    setVisible(false);
  };

  const [createReleaseNote] = useMutation(MUTATION_CREATE_RELEASE_NOTE, {
    fetchPolicy: 'network-only',
    onCompleted: res => {
      if (res?.createReleaseNote?.releaseNote?.id) {
        handleCancelModal();

        refetchData();

        if (scheduleModalRef.current.visible) {
          scheduleModalRef.current.handleCloseModal();
        }
      }
    },
  });

  const handleCreateReleaseNote = status => {
    validateFields((err, values) => {
      if (!err) {
        if (status === PUBLISHED) {
          createReleaseNote({
            variables: {
              input: {
                ...values,
                status,
              },
            },
          });
        } else {
          scheduleModalRef.current.handleOpenModal();
        }
      }
    });
  };

  const handleSchedulePublish = ({ publishedAt }) => {
    const values = getFieldsValue();

    createReleaseNote({
      variables: {
        input: {
          ...values,
          publishedAt,
          status: DRAFT,
        },
      },
    });
  };

  useImperativeHandle(ref, () => ({
    handleOpenModal: () => {
      setVisible(true);
    },
  }));

  return (
    <Modal
      visible={visible}
      width={812}
      title={t('content.release_note.modal.create.title')}
      wrapClassName={classes.wrapper}
      onCancel={handleCancelModal}
      destroyOnClose={true}
      footer={
        <>
          <Button
            type="default"
            size={'large'}
            className={classes.defaultBtn}
            onClick={handleCancelModal}
          >
            {t('content.release_note.modal.create.btn.cancel')}
          </Button>
          <Button
            type="default"
            size={'large'}
            className={classes.defaultBtn}
            onClick={() => handleCreateReleaseNote(DRAFT)}
          >
            {t('content.release_note.modal.create.btn.schedule')}
          </Button>
          <Button
            type="primary"
            size={'large'}
            className={classes.primaryBtn}
            onClick={() => handleCreateReleaseNote(PUBLISHED)}
          >
            {t('content.release_note.modal.create.btn.publish')}
          </Button>
        </>
      }
    >
      <Form>
        <Row className={classes.container}>
          <Col span={24}>
            <Form.Item label={t('content.release_note.modal.create.label.title')}>
              {getFieldDecorator('title', {
                rules: [
                  {
                    required: true,
                    message: t('content.release_note.modal.create.error.empty'),
                  },
                  {
                    max: 100,
                    type: 'string',
                    message: t('global.form.input.error.limit', {
                      number: 100,
                    }),
                  },
                ],
              })(<Input placeholder={t('content.release_note.modal.create.placeholder.title')} />)}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label={t('content.release_note.modal.create.label.content')}>
              {getFieldDecorator('content', {
                rules: [
                  {
                    required: true,
                    message: t('content.release_note.modal.create.error.empty'),
                  },
                  {
                    max: 500,
                    type: 'string',
                    message: t('global.form.input.error.limit', {
                      number: 500,
                    }),
                  },
                ],
              })(
                <Input.TextArea
                  rows={5}
                  placeholder={t('content.release_note.modal.create.placeholder.content')}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={
                <>
                  <span>{t('content.release_note.modal.create.label.article')}</span>&nbsp;
                  <span className="optional-label">
                    {t('content.release_note.modal.create.label.optional')}
                  </span>
                </>
              }
            >
              {getFieldDecorator('articleId')(<Articles />)}
            </Form.Item>
          </Col>

          <ScheduleModal
            wrappedComponentRef={form => (scheduleModalRef.current = form)}
            handleSuccessCallback={handleSchedulePublish}
          />
        </Row>
      </Form>
    </Modal>
  );
});

export default Form.create({
  name: 'create-release-modal',
})(CreateReleaseModal);
